// src/lib/data/strategies/LocalStrategy.js - V10 ENTERPRISE ULTRA-ROBUST
// ======================================================
// 📦 LOCAL STRATEGY - NIVEL ENTERPRISE + TOMBSTONE SYSTEM
// ======================================================
// 🔥 V10: invalidateCache(collection) — método público para DataContext V8
// 🔥 V10: create/update/delete invalidan caché de colección automáticamente
// 🔥 V10: _safeWriteDB también invalida _dbCache para datos 100% frescos
// 🔥 V10.1: FIX — createMessage en fallback instance para mensajes
// 🔥 V10.1: FIX — Notificación a listeners después de _fallbackCreate
// 🔥 V9: INDEXEDDB MIGRATION — _safeReadDB/_safeWriteDB usan IndexedDB
// 🔥 V9: AUTO-MIGRATION — Migra datos de localStorage → IndexedDB una sola vez
// 🔥 V9: DUAL STORAGE — IndexedDB primario, localStorage fallback automático
// 🔥 V9: CERO BREAKING CHANGES — Misma API, mismo comportamiento
// 🔥 V8: ANTI-DUPLICADOS — _fallbackCreate() verifica antes de insertar
// 🔥 V8: ANTI-DUPLICADOS — create() con doble verificación ID + título
// 🔥 V8: ANTI-DUPLICADOS — update() busca por id, uid, y _localId
// ======================================================

import IDataStrategy from './IDataStrategy.js';
import { saveUserData, getUserData } from '../../storage/indexedDB.js';

// Constantes de configuración (calibradas para producción)
const TOMBSTONE_TTL_DAYS = 30;
const TOMBSTONE_CLEANUP_INTERVAL = 100;
const MEMORY_CACHE_TTL_MS = 30000;
const DEDUPE_CLEANUP_INTERVAL = 50;

let _operationCounter = 0;

class LocalStrategy extends IDataStrategy {
  constructor(companyId, userId) {
    super();
    
    if (!userId || userId === 'anonymous') {
      userId = this._extractUserIdFromStorage();
    }
    
    this.companyId = companyId || localStorage.getItem('current_company') || 'default_company';
    this.userId = userId || '1';
    
    if (this.userId === 'anonymous') {
      console.error('❌ CRÍTICO: LocalStrategy usando userId = "anonymous"');
      this.userId = '1';
    }
    
    this.instance = null;
    this.cache = new Map();
    this.dbKey = `imoveispro_db_user_${this.userId}`;
    
    // 🔥 V9: Flags de control de IndexedDB
    this._indexedDBReady = false;
    this._indexedDBFailed = false;
    this._migrationDone = false;
    this._dbCache = null;
    
    // 🔥 V7: Caché de tombstones en memoria
    this._tombstoneMemoryCache = {
      byCollection: new Map(),
      lastFullRefresh: 0
    };
    
    // 🔥 V10.1: Listeners para notificar cambios a DataContext
    this._listeners = [];
    
    this._ensureTombstoneStore();
    
    this.init();
  }
  
  // ======================================================
  // 🔥 V10.1: SISTEMA DE LISTENERS PARA DATACONTEXT
  // ======================================================
  
  addListener(fn) {
    if (typeof fn === 'function' && !this._listeners.includes(fn)) {
      this._listeners.push(fn);
    }
  }
  
  removeListener(fn) {
    this._listeners = this._listeners.filter(l => l !== fn);
  }
  
  _notifyListeners() {
    const db = this._safeReadDB();
    const state = {
      clients: db.clients || [],
      properties: db.properties || [],
      tasks: db.tasks || [],
      calendarEvents: db.calendarEvents || [],
      documents: db.documents || [],
      financeiro: db.financeiro || [],
      messages: db.messages || [],
      interactions: db.interactions || [],
      team_members: db.team_members || [],
      team_invites: db.team_invites || [],
    };
    
    this._listeners.forEach(fn => {
      try {
        fn(state);
      } catch (e) {
        console.warn('⚠️ Error en listener:', e.message);
      }
    });
  }
  
  // ======================================================
  // 🔥 V10: INVALIDACIÓN DE CACHÉ — MÉTODO PÚBLICO
  // ======================================================
  
  invalidateCache(collectionName) {
    if (!collectionName) {
      this.cache.clear();
      this._dbCache = null;
      return;
    }
    
    const prefix = `${this.userId}_${collectionName}`;
    const keysToDelete = [];
    
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key));
    this._dbCache = null;
  }
  
  // ======================================================
  // 🔧 MÉTODOS DE EXTRACCIÓN
  // ======================================================
  
  _extractUserIdFromStorage() {
    try {
      const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
      if (authUser?.id) return String(authUser.id);
    } catch (e) { /* silencioso */ }
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith('imoveispro_db_user_')) {
        return key.replace('imoveispro_db_user_', '');
      }
    }
    return '1';
  }
  
  // ======================================================
  // 🔥 V9: MIGRACIÓN AUTOMÁTICA LOCALSTORAGE → INDEXEDDB
  // ======================================================
  
  async _migrateToIndexedDB() {
    if (this._migrationDone || this._indexedDBFailed) return;
    
    try {
      const raw = localStorage.getItem(this.dbKey);
      if (!raw) {
        this._migrationDone = true;
        return;
      }
      
      const existing = await getUserData(this.userId);
      if (existing && Object.keys(existing).length > 3) {
        const localTimestamp = this._getLocalStorageTimestamp(raw);
        const indexedTimestamp = existing._indexedDB_updatedAt 
          ? new Date(existing._indexedDB_updatedAt).getTime() 
          : 0;
        
        if (indexedTimestamp >= localTimestamp) {
          this._migrationDone = true;
          return;
        }
      }
      
      const data = JSON.parse(raw);
      await saveUserData(this.userId, data);
      this._migrationDone = true;
      this._indexedDBReady = true;
    } catch (error) {
      console.warn('⚠️ [V10] Error en migración a IndexedDB:', error.message);
      this._indexedDBFailed = true;
      this._migrationDone = true;
    }
  }
  
  _getLocalStorageTimestamp(raw) {
    try {
      const data = JSON.parse(raw);
      let maxTime = 0;
      const collections = ['properties', 'clients', 'tasks', 'calendarEvents', 'documents', 'financeiro', 'messages'];
      for (const col of collections) {
        if (Array.isArray(data[col])) {
          for (const item of data[col]) {
            const t = new Date(item.updatedAt || item.createdAt || 0).getTime();
            if (t > maxTime) maxTime = t;
          }
        }
      }
      return maxTime;
    } catch {
      return 0;
    }
  }
  
  // ======================================================
  // 🔥 V10: HELPERS ATÓMICOS DE LECTURA/ESCRITURA
  // ======================================================
  
  _safeReadDB() {
    if (this._dbCache) return this._dbCache;
    
    try {
      const raw = localStorage.getItem(this.dbKey);
      if (!raw) return this._emptyDB();
      const parsed = JSON.parse(raw);
      const db = this._normalizeDB(parsed);
      this._dbCache = db;
      return db;
    } catch (error) {
      console.warn('⚠️ Error leyendo DB:', error.message);
      return this._emptyDB();
    }
  }
  
  _safeWriteDB(db) {
    try {
      localStorage.setItem(this.dbKey, JSON.stringify(db));
      this._dbCache = db;
      
      // 🔥 V10.1: Notificar a listeners después de escribir
      this._notifyListeners();
      
      return true;
    } catch (error) {
      console.error('❌ [V10] Error escribiendo en localStorage:', error.message);
      return false;
    }
  }
  
  _emptyDB() {
    return {
      properties: [], clients: [], tasks: [], calendarEvents: [],
      documents: [], financeiro: [], messages: [], interactions: [],
      _tombstones: [],
      company: { nome: 'Imobiliária', cor: '#2563eb', logo: null, slogan: '', telefone: '', email: '' },
      userPreferences: {}
    };
  }
  
  _normalizeDB(db) {
    const defaults = this._emptyDB();
    for (const key of Object.keys(defaults)) {
      if (db[key] === undefined) db[key] = defaults[key];
      if (Array.isArray(defaults[key]) && !Array.isArray(db[key])) db[key] = [];
    }
    return db;
  }

  // ======================================================
  // 🔥 V10: DEDUPLICACIÓN AUTOMÁTICA
  // ======================================================
  
  _deduplicateCollection(collectionName) {
    const db = this._safeReadDB();
    const items = db[collectionName] || [];
    if (items.length <= 1) return 0;
    
    const seen = new Set();
    const cleaned = [];
    let dupesFound = 0;
    
    for (const item of items) {
      const id = String(item?.id || '');
      if (!id || seen.has(id)) {
        dupesFound++;
        continue;
      }
      seen.add(id);
      cleaned.push(item);
    }
    
    if (dupesFound > 0) {
      db[collectionName] = cleaned;
      this._safeWriteDB(db);
    }
    
    return dupesFound;
  }
  
  _findItemIndex(items, id) {
    const strId = String(id);
    return items.findIndex(item => 
      String(item?.id) === strId || 
      String(item?.uid) === strId || 
      String(item?._localId) === strId ||
      String(item?._firestoreId) === strId
    );
  }
  
  _findItemByContent(items, data, collection) {
    if (collection === 'clients') {
      const email = data.email?.toLowerCase()?.trim();
      const nome = data.name?.toLowerCase()?.trim();
      const telefone = data.phone?.replace(/\D/g, '');
      
      return items.findIndex(c => {
        if (email && c.email?.toLowerCase()?.trim() === email) return true;
        if (nome && telefone && c.name?.toLowerCase()?.trim() === nome && 
            c.phone?.replace(/\D/g, '') === telefone) return true;
        return false;
      });
    }
    
    if (collection === 'properties' && data.title) {
      const title = data.title.toLowerCase().trim();
      return items.findIndex(p => 
        p.title?.toLowerCase().trim() === title && 
        String(p.id) !== String(data.id)
      );
    }
    
    if (collection === 'tasks' && data.title) {
      const title = data.title.toLowerCase().trim();
      return items.findIndex(t => 
        t.title?.toLowerCase().trim() === title && 
        String(t.id) !== String(data.id)
      );
    }
    
    return -1;
  }
  
  // ======================================================
  // 🔥 V10: SISTEMA DE TOMBSTONES
  // ======================================================
  
  _ensureTombstoneStore() {
    const db = this._safeReadDB();
    if (!Array.isArray(db._tombstones)) {
      db._tombstones = [];
      this._safeWriteDB(db);
    }
  }
  
  _invalidateMemoryCache(collection = null) {
    if (collection) {
      this._tombstoneMemoryCache.byCollection.delete(collection);
    } else {
      this._tombstoneMemoryCache.byCollection.clear();
      this._tombstoneMemoryCache.lastFullRefresh = 0;
    }
  }
  
  async createTombstone(collection, recordId, metadata = {}) {
    if (!this.instance) await this.init();
    
    if (!collection || !recordId) {
      console.error('❌ createTombstone: collection y recordId son obligatorios');
      return null;
    }
    
    const db = this._safeReadDB();
    const normalizedId = String(recordId);
    const now = new Date();
    
    const existingIndex = (db._tombstones || []).findIndex(
      t => t.collection === collection && t.recordId === normalizedId
    );
    
    const tombstone = {
      id: `tombstone_${collection}_${normalizedId}_${Date.now()}`,
      collection,
      recordId: normalizedId,
      deletedAt: now.toISOString(),
      expiresAt: new Date(now.getTime() + TOMBSTONE_TTL_DAYS * 24 * 60 * 60 * 1000).toISOString(),
      userId: metadata.userId || this.userId,
      deletedBy: metadata.deletedBy || this.userId,
      reason: metadata.reason || 'user_delete',
      _version: metadata._version || 1,
      _tombstoneVersion: 'V10'
    };
    
    if (existingIndex !== -1) {
      db._tombstones[existingIndex] = tombstone;
    } else {
      db._tombstones.push(tombstone);
    }
    
    this._safeWriteDB(db);
    this._invalidateMemoryCache(collection);
    
    _operationCounter++;
    if (_operationCounter % TOMBSTONE_CLEANUP_INTERVAL === 0) {
      this._cleanupExpiredTombstones();
    }
    
    return tombstone;
  }
  
  async isTombstoned(collection, recordId) {
    if (!collection || !recordId) return false;
    
    const normalizedId = String(recordId);
    const cached = this._tombstoneMemoryCache.byCollection.get(collection);
    if (cached && (Date.now() - cached.timestamp) < MEMORY_CACHE_TTL_MS) {
      return cached.ids.has(normalizedId);
    }
    
    try {
      const db = this._safeReadDB();
      const now = Date.now();
      
      return (db._tombstones || []).some(t => {
        if (t.collection !== collection) return false;
        if (t.recordId !== normalizedId) return false;
        if (t.expiresAt && new Date(t.expiresAt).getTime() < now) return false;
        return true;
      });
    } catch (error) {
      return false;
    }
  }
  
  async getActiveTombstones(collection = null) {
    const db = this._safeReadDB();
    const now = Date.now();
    
    let active = (db._tombstones || []).filter(t => {
      if (t.expiresAt && new Date(t.expiresAt).getTime() < now) return false;
      return true;
    });
    
    if (collection) {
      active = active.filter(t => t.collection === collection);
    }
    
    return active;
  }
  
  async getTombstonedIds(collection) {
    if (!collection) return new Set();
    
    const cached = this._tombstoneMemoryCache.byCollection.get(collection);
    if (cached && (Date.now() - cached.timestamp) < MEMORY_CACHE_TTL_MS) {
      return cached.ids;
    }
    
    const tombstones = await this.getActiveTombstones(collection);
    const ids = new Set(tombstones.map(t => t.recordId));
    
    this._tombstoneMemoryCache.byCollection.set(collection, {
      ids,
      timestamp: Date.now()
    });
    
    return ids;
  }
  
  async removeTombstone(collection, recordId) {
    const db = this._safeReadDB();
    const before = (db._tombstones || []).length;
    
    db._tombstones = (db._tombstones || []).filter(
      t => !(t.collection === collection && t.recordId === String(recordId))
    );
    
    if (before !== db._tombstones.length) {
      this._safeWriteDB(db);
      this._invalidateMemoryCache(collection);
      return true;
    }
    return false;
  }
  
  _cleanupExpiredTombstones() {
    const db = this._safeReadDB();
    const now = Date.now();
    const before = (db._tombstones || []).length;
    
    db._tombstones = (db._tombstones || []).filter(t => {
      if (!t.expiresAt) return true;
      return new Date(t.expiresAt).getTime() >= now;
    });
    
    if (before !== db._tombstones.length) {
      this._safeWriteDB(db);
      this._invalidateMemoryCache();
    }
  }
  
  _countTombstones() {
    return (this._safeReadDB()._tombstones || []).length;
  }
  
  // ======================================================
  // 🔄 INICIALIZACIÓN
  // ======================================================
  
  async init() {
    try {
      if (typeof window !== 'undefined') {
        const databaseModule = await import('../../../services/database.js');
        if (databaseModule?.initDatabase) {
          this.instance = databaseModule.initDatabase(this.userId, this.companyId);
          this._ensureCollectionsExist();
          this._cleanupAllDuplicates();
          return;
        }
      }
    } catch (error) {
      console.warn('⚠️ database.js no disponible, usando fallback local');
    }
    this.instance = this.createFallbackInstance();
    this._cleanupAllDuplicates();
  }
  
  _cleanupAllDuplicates() {
    const collections = ['properties', 'clients', 'tasks', 'calendarEvents', 'documents', 'financeiro', 'messages', 'interactions'];
    let totalRemoved = 0;
    for (const col of collections) {
      totalRemoved += this._deduplicateCollection(col);
    }
  }
  
  _ensureCollectionsExist() {
    const db = this._safeReadDB();
    let changed = false;
    
    const collections = ['clients', 'properties', 'tasks', 'calendarEvents', 'documents', 'financeiro', 'messages', 'interactions'];
    collections.forEach(col => {
      if (!Array.isArray(db[col])) { db[col] = []; changed = true; }
    });
    
    if (!Array.isArray(db._tombstones)) { db._tombstones = []; changed = true; }
    
    if (changed) this._safeWriteDB(db);
  }
  
  // ======================================================
  // 📦 FALLBACK INSTANCE — V10.1 CON createMessage
  // ======================================================
  
  createFallbackInstance() {
    const self = this;
    
    const _getCollection = (collectionName) => {
      const db = self._safeReadDB();
      const items = db[collectionName] || [];
      const tombstones = db._tombstones || [];
      const tombstonedIds = new Set(
        tombstones.filter(t => t.collection === collectionName).map(t => t.recordId)
      );
      const filtered = tombstonedIds.size === 0 
        ? items 
        : items.filter(item => !tombstonedIds.has(String(item.id)));
      
      const seen = new Set();
      return filtered.filter(item => {
        const id = String(item?.id || '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    };
    
    const _getById = (collectionName, id) => {
      const db = self._safeReadDB();
      const isTombstoned = (db._tombstones || []).some(
        t => t.collection === collectionName && t.recordId === String(id)
      );
      if (isTombstoned) return null;
      const strId = String(id);
      return (db[collectionName] || []).find(item => 
        String(item.id) === strId || String(item.uid) === strId || String(item._localId) === strId
      ) || null;
    };
    
    const _createItem = (collectionName, data, idPrefix = '') => {
      const db = self._safeReadDB();
      
      const dataId = data.id || data._localId || data.uid;
      if (dataId) {
        const existingIndex = self._findItemIndex(db[collectionName] || [], dataId);
        if (existingIndex >= 0) {
          db[collectionName][existingIndex] = { 
            ...db[collectionName][existingIndex], 
            ...data, 
            updatedAt: new Date().toISOString() 
          };
          self._safeWriteDB(db);
          return db[collectionName][existingIndex];
        }
      }
      
      const contentMatch = self._findItemByContent(db[collectionName] || [], data, collectionName);
      if (contentMatch >= 0) {
        db[collectionName][contentMatch] = { 
          ...db[collectionName][contentMatch], 
          ...data, 
          updatedAt: new Date().toISOString() 
        };
        self._safeWriteDB(db);
        return db[collectionName][contentMatch];
      }
      
      if (data.id) {
        const tombstoneIndex = (db._tombstones || []).findIndex(
          t => t.collection === collectionName && t.recordId === String(data.id)
        );
        if (tombstoneIndex !== -1) {
          db._tombstones.splice(tombstoneIndex, 1);
          self._invalidateMemoryCache(collectionName);
        }
      }
      
      const newItem = {
        id: data.id || `${idPrefix || collectionName.slice(0, 4)}_${Date.now()}`,
        userId: self.userId,
        companyId: self.companyId,
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...data
      };
      
      db[collectionName] = db[collectionName] || [];
      db[collectionName].push(newItem);
      self._safeWriteDB(db);
      return newItem;
    };
    
    const _updateItem = (collectionName, id, data, preserveImages = false) => {
      const db = self._safeReadDB();
      const items = db[collectionName] || [];
      
      const index = self._findItemIndex(items, id);
      
      if (index === -1) return null;
      
      const existing = items[index];
      const merged = { ...existing, ...data, updatedAt: new Date().toISOString() };
      
      if (preserveImages && (!data.images || data.images.length === 0)) {
        merged.images = existing.images || [];
      }
      
      items[index] = merged;
      self._safeWriteDB(db);
      return merged;
    };
    
    const _deleteItem = async (collectionName, id) => {
      await self.createTombstone(collectionName, id);
      const db = self._safeReadDB();
      const before = (db[collectionName] || []).length;
      const strId = String(id);
      db[collectionName] = (db[collectionName] || []).filter(
        item => String(item.id) !== strId && 
                String(item.uid) !== strId && 
                String(item._localId) !== strId
      );
      self._safeWriteDB(db);
      return before > db[collectionName].length;
    };
    
    return {
      getProperties: () => _getCollection('properties'),
      getPropertyById: (id) => _getById('properties', id),
      createProperty: (data) => _createItem('properties', data, 'prop_'),
      updateProperty: (id, data) => _updateItem('properties', id, data, true),
      deleteProperty: (id) => _deleteItem('properties', id),
      
      getClients: () => _getCollection('clients'),
      getClientById: (id) => _getById('clients', id),
      createClient: (data) => _createItem('clients', data, 'client_'),
      updateClient: (id, data) => _updateItem('clients', id, data),
      deleteClient: (id) => _deleteItem('clients', id),
      
      getTasks: () => _getCollection('tasks'),
      getTaskById: (id) => _getById('tasks', id),
      createTask: (data) => _createItem('tasks', data, 'task_'),
      updateTask: (id, data) => _updateItem('tasks', id, data),
      deleteTask: (id) => _deleteItem('tasks', id),
      
      getCalendarEvents: () => _getCollection('calendarEvents'),
      createCalendarEvent: (data) => _createItem('calendarEvents', data, 'event_'),
      
      getDocuments: () => _getCollection('documents'),
      createDocument: (data) => _createItem('documents', data, 'doc_'),
      
      getFinanceiro: () => _getCollection('financeiro'),
      createFinanceiro: (data) => _createItem('financeiro', data, 'fin_'),
      
      // 🔥 V10.1: AGREGADO createMessage
      getMessages: () => _getCollection('messages'),
      createMessage: (data) => _createItem('messages', data, 'msg_'),
      deleteMessage: (id) => _deleteItem('messages', id),
      
      getCompany: () => {
        const db = self._safeReadDB();
        return db.company || { nome: 'Imobiliária', cor: '#2563eb' };
      },
      saveCompany: (data) => {
        const db = self._safeReadDB();
        db.company = { ...db.company, ...data, updatedAt: new Date().toISOString() };
        self._safeWriteDB(db);
        return db.company;
      },
      
      getDashboardStats: () => {
        const properties = _getCollection('properties');
        const clients = _getCollection('clients');
        const tasks = _getCollection('tasks');
        const financeiro = self._safeReadDB().financeiro || [];
        
        const now = new Date();
        const cm = now.getMonth();
        const cy = now.getFullYear();
        
        const monthlyRevenue = financeiro
          .filter(f => f.type === 'receita' && new Date(f.date || f.createdAt).getMonth() === cm && new Date(f.date || f.createdAt).getFullYear() === cy)
          .reduce((s, f) => s + (Number(f.value) || 0), 0);
        
        const clientsWithProps = clients.filter(c => c.propertyId || (c.propertyIds?.length > 0)).length;
        
        return {
          activeProperties: properties.filter(p => ['Disponível', 'available', 'active'].includes(p.status)).length,
          activeClients: clients.filter(c => ['Ativo', 'active'].includes(c.status)).length,
          pendingTasks: tasks.filter(t => ['pending', 'pendente', 'in_progress'].includes(t.status)).length,
          totalProperties: properties.length,
          totalClients: clients.length,
          totalTasks: tasks.length,
          monthlyRevenue,
          conversionRate: clients.length > 0 ? Math.round((clientsWithProps / clients.length) * 100) : 0,
          source: 'local_strategy_fallback_v10',
          timestamp: new Date().toISOString()
        };
      },
      
      getMonthlySales: (months = 6) => {
        const financeiro = self._safeReadDB().financeiro || [];
        const result = [];
        const today = new Date();
        for (let i = months - 1; i >= 0; i--) {
          const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
          const monthStr = date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
          const sales = financeiro.filter(f => {
            if (f.type !== 'receita' || f.category !== 'Venda de Imóvel') return false;
            const d = new Date(f.date || f.createdAt);
            return d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
          });
          result.push({ month: monthStr, value: sales.reduce((s, f) => s + (Number(f.value) || 0), 0), fullDate: date });
        }
        return result;
      },
      
      getMonthlyRentals: (months = 6) => {
        const financeiro = self._safeReadDB().financeiro || [];
        const result = [];
        const today = new Date();
        for (let i = months - 1; i >= 0; i--) {
          const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
          const monthStr = date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
          const rentals = financeiro.filter(f => {
            if (f.category !== 'Aluguel') return false;
            const d = new Date(f.date || f.createdAt);
            return d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
          });
          result.push({ month: monthStr, monthlyValue: rentals.reduce((s, f) => s + (Number(f.value) || 0), 0), count: rentals.length, fullDate: date });
        }
        return result;
      },
      
      getSalesGrowth: () => {
        const monthly = self.instance?.getMonthlySales?.(2) || [];
        if (monthly.length < 2) return { percentage: 0, trend: 'stable', formatted: '0%' };
        const prev = monthly[0]?.value || 0;
        const curr = monthly[1]?.value || 0;
        if (prev === 0) return { percentage: curr > 0 ? 100 : 0, trend: curr > 0 ? 'up' : 'stable', formatted: curr > 0 ? '+100%' : '0%' };
        const g = ((curr - prev) / prev) * 100;
        const r = Math.round(g * 10) / 10;
        return { percentage: r, trend: r > 0 ? 'up' : r < 0 ? 'down' : 'stable', formatted: `${r > 0 ? '+' : ''}${r}%` };
      },
      
      getUserPreferences: () => self._safeReadDB().userPreferences || {},
      saveUserPreferences: (data) => {
        const db = self._safeReadDB();
        db.userPreferences = { ...db.userPreferences, ...data, updatedAt: new Date().toISOString() };
        self._safeWriteDB(db);
        return db.userPreferences;
      }
    };
  }
  
  // ======================================================
  // 🟢 MÉTODOS DE DASHBOARD
  // ======================================================
  
  async getDashboardStats(userId) {
    if (!this.instance) await this.init();
    
    try {
      if (this.instance?.getDashboardStats) {
        const stats = await this.instance.getDashboardStats();
        return { ...stats, source: 'local_strategy_v10' };
      }
    } catch (e) { /* fallback abajo */ }
    
    return this._calculateLocalStats();
  }
  
  _calculateLocalStats() {
    try {
      const properties = (this.instance?.getProperties?.() || []).filter(p => {
        const db = this._safeReadDB();
        return !(db._tombstones || []).some(t => t.collection === 'properties' && t.recordId === String(p.id));
      });
      const clients = (this.instance?.getClients?.() || []).filter(c => {
        const db = this._safeReadDB();
        return !(db._tombstones || []).some(t => t.collection === 'clients' && t.recordId === String(c.id));
      });
      const tasks = (this.instance?.getTasks?.() || []).filter(t => {
        const db = this._safeReadDB();
        return !(db._tombstones || []).some(tb => tb.collection === 'tasks' && tb.recordId === String(t.id));
      });
      const financeiro = this._safeReadDB().financeiro || [];
      
      const now = new Date();
      const cm = now.getMonth();
      const cy = now.getFullYear();
      
      return {
        activeProperties: properties.filter(p => ['Disponível', 'available', 'active'].includes(p.status)).length,
        activeClients: clients.filter(c => ['Ativo', 'active'].includes(c.status)).length,
        pendingTasks: tasks.filter(t => ['pending', 'pendente', 'in_progress'].includes(t.status)).length,
        totalProperties: properties.length,
        totalClients: clients.length,
        totalTasks: tasks.length,
        monthlyRevenue: financeiro.filter(f => f.type === 'receita' && new Date(f.date || f.createdAt).getMonth() === cm && new Date(f.date || f.createdAt).getFullYear() === cy).reduce((s, f) => s + (Number(f.value) || 0), 0),
        conversionRate: clients.length > 0 ? Math.round((clients.filter(c => c.propertyId || c.propertyIds?.length > 0).length / clients.length) * 100) : 0,
        source: 'local_direct_v10',
        timestamp: new Date().toISOString()
      };
    } catch (e) {
      return { activeProperties: 0, activeClients: 0, pendingTasks: 0, totalProperties: 0, totalClients: 0, totalTasks: 0, monthlyRevenue: 0, conversionRate: 0, source: 'local_error', error: e.message, timestamp: new Date().toISOString() };
    }
  }
  
  // ======================================================
  // 📖 CRUD — V10.1 CON INVALIDACIÓN DE CACHÉ Y NOTIFICACIÓN
  // ======================================================
  
  async create(collection, data, options = {}) {
    if (!this.instance) await this.init();
    
    _operationCounter++;
    if (_operationCounter % DEDUPE_CLEANUP_INTERVAL === 0) {
      this._deduplicateCollection(collection);
    }
    
    const methodMap = {
      'clients': () => this.instance.createClient?.(data) || this._fallbackCreate('clients', data),
      'properties': () => this.instance.createProperty?.(data) || this._fallbackCreate('properties', data),
      'tasks': () => this.instance.createTask?.(data) || this._fallbackCreate('tasks', data),
      'calendarEvents': () => this.instance.createCalendarEvent?.(data) || this._fallbackCreate('calendarEvents', data),
      'documents': () => this.instance.createDocument?.(data) || this._fallbackCreate('documents', data),
      'financeiro': () => this.instance.createFinanceiro?.(data) || this._fallbackCreate('financeiro', data),
      // 🔥 V10.1: createMessage ahora SÍ funciona porque lo agregamos al fallback
      'messages': () => this.instance.createMessage?.(data) || this._fallbackCreate('messages', data)
    };
    
    if (methodMap[collection]) {
      const result = await methodMap[collection]();
      this.invalidateCache(collection);
      if (options.cache !== false && result?.id) this.cache.set(`${collection}:${result.id}`, result);
      return result;
    }
    const result = this._fallbackCreate(collection, data);
    this.invalidateCache(collection);
    return result;
  }
  
  _fallbackCreate(collection, data) {
    const db = this._safeReadDB();
    db[collection] = db[collection] || [];
    
    const dataId = data.id || data._localId || data.uid;
    if (dataId) {
      const existingIndex = this._findItemIndex(db[collection], dataId);
      if (existingIndex >= 0) {
        db[collection][existingIndex] = { 
          ...db[collection][existingIndex], 
          ...data, 
          updatedAt: new Date().toISOString() 
        };
        this._safeWriteDB(db);
        return db[collection][existingIndex];
      }
    }
    
    const contentMatch = this._findItemByContent(db[collection], data, collection);
    if (contentMatch >= 0) {
      db[collection][contentMatch] = { 
        ...db[collection][contentMatch], 
        ...data, 
        updatedAt: new Date().toISOString() 
      };
      this._safeWriteDB(db);
      return db[collection][contentMatch];
    }
    
    // 🔥 V10.1: Para mensajes, SIEMPRE generar un ID único con timestamp
    const newId = data.id || (collection === 'messages' 
      ? `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      : `${collection.slice(0, 4)}_${Date.now()}`);
    
    const newItem = {
      id: newId,
      userId: this.userId, 
      companyId: this.companyId,
      createdAt: data.createdAt || new Date().toISOString(), 
      ...data
    };
    db[collection].push(newItem);
    this._safeWriteDB(db);
    return newItem;
  }
  
  async read(collection, query = {}, options = {}) {
    if (!this.instance) await this.init();
    
    const cacheKey = `${this.userId}_${collection}:${JSON.stringify(query)}`;
    if (options.cache !== false && this.cache.has(cacheKey)) return this.cache.get(cacheKey);
    
    const methodMap = {
      'clients': () => this.instance.getClients?.(),
      'properties': () => this.instance.getProperties?.(),
      'tasks': () => this.instance.getTasks?.(),
      'calendarEvents': () => this.instance.getCalendarEvents?.(),
      'documents': () => this.instance.getDocuments?.(),
      'financeiro': () => this.instance.getFinanceiro?.(),
      'messages': () => this.instance.getMessages?.(),
      'company': () => this.instance.getCompany?.(),
      'dashboard': () => this.instance.getDashboardStats?.()
    };
    
    let result = methodMap[collection] ? (await methodMap[collection]() || []) : (this._safeReadDB()[collection] || []);
    
    if (Array.isArray(result)) {
      const seen = new Set();
      result = result.filter(item => {
        const id = String(item?.id || '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    }
    
    if (query.id && Array.isArray(result)) {
      result = result.find(item => String(item.id) === String(query.id)) || null;
    }
    if (query.where && Array.isArray(result)) {
      result = result.filter(item => Object.entries(query.where).every(([k, v]) => item[k] === v));
    }
    
    if (options.cache !== false && result !== null) this.cache.set(cacheKey, result);
    return result;
  }
  
  async update(collection, id, data, options = {}) {
    if (!this.instance) await this.init();
    
    let existingItem = null;
    try {
      if (collection === 'properties') existingItem = this.instance.getPropertyById?.(id);
      else if (collection === 'clients') existingItem = this.instance.getClientById?.(id);
    } catch (e) { /* silencioso */ }
    
    let merged = { ...data };
    if (existingItem) {
      if (collection === 'properties' && (!data.images || data.images.length === 0)) {
        merged.images = existingItem.images || [];
      }
      ['createdAt', 'userId', 'id'].forEach(f => {
        if (data[f] === undefined && existingItem[f] !== undefined) merged[f] = existingItem[f];
      });
    }
    
    const methodMap = {
      'properties': () => this.instance.updateProperty?.(id, merged),
      'clients': () => this.instance.updateClient?.(id, merged),
      'tasks': () => this.instance.updateTask?.(id, merged),
      'company': () => this.instance.saveCompany?.(merged)
    };
    
    if (methodMap[collection]) {
      const result = await methodMap[collection]();
      this.invalidateCache(collection);
      return result || { id, ...merged, updatedAt: new Date().toISOString() };
    }
    
    const db = this._safeReadDB();
    const items = db[collection] || [];
    const index = this._findItemIndex(items, id);
    if (index === -1) return null;
    
    const existing = items[index];
    const finalData = { ...existing, ...merged };
    if (collection === 'properties' && (!merged.images || merged.images.length === 0)) {
      finalData.images = existing.images || [];
    }
    finalData.updatedAt = new Date().toISOString();
    items[index] = finalData;
    db[collection] = items;
    this._safeWriteDB(db);
    this.invalidateCache(collection);
    return items[index];
  }
  
  async delete(collection, id, options = {}) {
    if (!this.instance) await this.init();
    
    await this.createTombstone(collection, id, {
      userId: this.userId,
      reason: options.reason || 'user_delete',
      _version: options._version || 1
    });
    
    const methodMap = {
      'properties': () => this.instance.deleteProperty?.(id),
      'clients': () => this.instance.deleteClient?.(id),
      'tasks': () => this.instance.deleteTask?.(id)
    };
    
    if (methodMap[collection]) {
      await methodMap[collection]();
      this.invalidateCache(collection);
      return { success: true, id, tombstone: true };
    }
    
    const db = this._safeReadDB();
    const strId = String(id);
    const before = (db[collection] || []).length;
    db[collection] = (db[collection] || []).filter(
      item => String(item.id) !== strId && 
              String(item.uid) !== strId && 
              String(item._localId) !== strId
    );
    this._safeWriteDB(db);
    this.invalidateCache(collection);
    return { success: before > db[collection].length, id, tombstone: true };
  }
  
  async list(collection, filters = {}, options = {}) {
    const items = await this.read(collection);
    if (!Array.isArray(items)) return [];
    return items.filter(item => Object.entries(filters).every(([k, v]) => item[k] === v));
  }
  
  // ======================================================
  // 🔍 DEBUG
  // ======================================================
  
  getCurrentPath() { return this.dbKey; }
  getIds() { return { companyId: this.companyId, userId: this.userId, dbKey: this.dbKey }; }
  
  async healthCheck() {
    return {
      status: this.instance ? 'healthy' : 'fallback',
      type: 'local',
      version: 'V10.1',
      userId: this.userId,
      companyId: this.companyId,
      dbKey: this.dbKey,
      instanceReady: !!this.instance,
      hasCreateMessage: typeof this.instance?.createMessage === 'function',
      cacheSize: this.cache.size,
      compatible: true,
      tombstones: this._countTombstones(),
      hasTombstoneSupport: true,
      antiDuplicates: true,
      dedupeOnInit: true,
      cacheInvalidation: true,
      listeners: this._listeners.length,
      memoryCacheSize: this._tombstoneMemoryCache.byCollection.size,
      indexedDBReady: this._indexedDBReady,
      indexedDBFailed: this._indexedDBFailed,
      migrationDone: this._migrationDone,
      storageType: this._indexedDBReady ? 'indexeddb' : 'localstorage',
      collections: Object.keys(this._safeReadDB()).filter(k => Array.isArray(this._safeReadDB()[k]))
    };
  }
}

export default LocalStrategy;